import React, { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { EyeIcon } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getListReturnPath, openRecord } from '../../hooks/useNavigateBack';
import { cn } from '../../lib/utils';

/** SPA open crew record with `state.from` pointing at the current report URL. */
export function useCrewReportOpen() {
  const navigate = useNavigate();
  const location = useLocation();
  const listReturnPath = getListReturnPath(location);
  const openCrew = useCallback(
    (crewId: number) => {
      openRecord(navigate, `/crew/${crewId}`, listReturnPath);
    },
    [listReturnPath, navigate],
  );
  return { listReturnPath, openCrew };
}

export const ReportTextCell: React.FC<{
  value: string | number | null | undefined;
  /** Shown when value is null/empty. Default em dash; pass "" for a blank cell. */
  empty?: string;
}> = ({ value, empty = '—' }) => {
  const { colors } = useTheme();
  const text = value == null || value === '' ? empty : String(value);
  return <span className={cn('text-sm whitespace-nowrap', colors.text)}>{text}</span>;
};

/** View action linking to the crew dashboard (`/crew/:id`). */
export const CrewReportViewAction: React.FC<{
  crewId: number;
  title?: string;
  /** When set, Back on the crew page returns here (e.g. list with filters). */
  from?: string;
}> = ({ crewId, title = 'View', from }) => {
  const { colors } = useTheme();
  return (
    <Link
      to={`/crew/${crewId}`}
      state={from ? { from } : undefined}
      className={cn('inline-flex p-1 rounded-lg transition-colors', colors.primaryFg, colors.interactiveHover)}
      onClick={(e) => e.stopPropagation()}
      title={title}
      aria-label={title}
    >
      <EyeIcon className="w-5 h-5" />
    </Link>
  );
};

/**
 * List frame matching GenericListPage — DataTable supplies the card chrome
 * (`rounded-lg shadow-sm border`). Do not wrap with another bordered card.
 */
export const CrewDataTableFrame: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div className={cn('flex-1 min-h-0 flex flex-col overflow-hidden', className)}>{children}</div>
);

/** Empty / prompt state with the same card chrome as DataTable. */
export const CrewDataTablePrompt: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colors } = useTheme();
  return (
    <div
      className={cn(
        'h-full min-h-[12rem] flex items-center justify-center text-sm rounded-lg shadow-sm border p-8',
        colors.surface,
        colors.border,
        colors.textSecondary,
      )}
    >
      {children}
    </div>
  );
};

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export const WITHIN_DAYS_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '365 days' },
];
